// apiComposerUtils.js

import { cleanString } from '../apiUtils';
import {
  isComposerTemporalFieldType,
  isComposerTemporalOperatorNeedsUnit,
  isComposerTemporalOperatorNeedsValue,
  normalizeComposerCalendarUnit,
  normalizeComposerRelativeUnit,
  normalizeComposerOperatorForFieldType,
  resolveComposerFieldType,
} from './apiComposerFilters';

function parseJsonObject(text, fallbackValue = {}) {
  const raw = cleanString(text);
  if (!raw) {
    return {
      value: fallbackValue,
      error: '',
    };
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {
        value: null,
        error: 'JSON payload must be an object.',
      };
    }

    return {
      value: parsed,
      error: '',
    };
  } catch (error) {
    return {
      value: null,
      error: error?.message || 'Invalid JSON payload.',
    };
  }
}

function parsePositiveInt(value, fallbackValue) {
  const parsed = Number.parseInt(cleanString(value), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallbackValue;
  return parsed;
}

function normalizePredicateValue(rawValue, fieldType) {
  const value = cleanString(rawValue);
  if (!value) return '';

  const normalizedType = cleanString(fieldType).toLowerCase();

  if (normalizedType.includes('bool')) {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
    return value;
  }

  if (normalizedType.includes('number') || normalizedType.includes('int') || normalizedType.includes('float') || normalizedType.includes('decimal')) {
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : value;
  }

  return value;
}

function normalizePredicateOperator(rawOperator) {
  const operator = cleanString(rawOperator).toLowerCase();
  if (!operator) return 'eq';

  if (operator === 'is' || operator === '==') return 'eq';
  if (operator === 'is_not' || operator === '!=') return 'neq';
  if (operator === 'is_any_of') return 'in';
  if (operator === 'is_none_of') return 'nin';

  return operator;
}

function parseListValue(rawValue) {
  if (Array.isArray(rawValue)) {
    return rawValue
      .map((value) => cleanString(value))
      .filter(Boolean);
  }

  const asText = cleanString(rawValue);
  if (!asText) return [];

  if (asText.startsWith('[') && asText.endsWith(']')) {
    try {
      const parsed = JSON.parse(asText);
      if (Array.isArray(parsed)) {
        return parsed
          .map((value) => cleanString(value))
          .filter(Boolean);
      }
    } catch (_error) {
      // Fall back to comma-separated parsing.
    }
  }

  return asText
    .split(',')
    .map((value) => cleanString(value))
    .filter(Boolean);
}

function parseBetweenValue(rawValue, fieldType) {
  if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
    return {
      min: normalizePredicateValue(rawValue.min, fieldType),
      max: normalizePredicateValue(rawValue.max, fieldType),
    };
  }

  if (Array.isArray(rawValue)) {
    return {
      min: normalizePredicateValue(rawValue[0], fieldType),
      max: normalizePredicateValue(rawValue[1], fieldType),
    };
  }

  const asText = cleanString(rawValue);
  if (!asText) {
    return {
      min: '',
      max: '',
    };
  }

  const [minRaw = '', maxRaw = ''] = asText.split(',');
  return {
    min: normalizePredicateValue(minRaw, fieldType),
    max: normalizePredicateValue(maxRaw, fieldType),
  };
}

function normalizeQueryPredicateValue({ rawValue, fieldType, operator }) {
  const normalizedOperator = normalizePredicateOperator(operator);

  if (normalizedOperator === 'is_empty' || normalizedOperator === 'is_not_empty') {
    return undefined;
  }

  if (normalizedOperator === 'exists') {
    const asText = cleanString(rawValue).toLowerCase();
    if (!asText) return true;
    if (asText === 'false' || asText === '0' || asText === 'no') return false;
    return true;
  }

  if (normalizedOperator === 'in' || normalizedOperator === 'nin') {
    return parseListValue(rawValue);
  }

  if (normalizedOperator === 'between') {
    return parseBetweenValue(rawValue, fieldType);
  }

  return normalizePredicateValue(rawValue, fieldType);
}

function normalizeTemporalPredicateParts({ rawValue, operator }) {
  const normalizedOperator = cleanString(operator).toLowerCase();
  const source = rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)
    ? rawValue
    : { value: rawValue, unit: null };

  if (!isComposerTemporalOperatorNeedsValue(normalizedOperator) && !isComposerTemporalOperatorNeedsUnit(normalizedOperator)) {
    return {
      value: undefined,
      unit: undefined,
    };
  }

  if (normalizedOperator === 'is_this') {
    const unit = normalizeComposerCalendarUnit(source?.unit) || 'week';
    return {
      value: undefined,
      unit,
    };
  }

  const rawNumeric = cleanString(source?.value);
  const numericValue = Number.parseInt(rawNumeric, 10);
  const value = Number.isFinite(numericValue) && numericValue > 0 ? numericValue : '';
  const unit = normalizeComposerRelativeUnit(source?.unit) || 'days';

  return {
    value,
    unit,
  };
}

function buildQueryPredicates({ queryFilters, fallbackFieldId, fallbackOperator, fallbackValue, fallbackFieldType }) {
  const rows = Array.isArray(queryFilters) && queryFilters.length > 0
    ? queryFilters
    : [
      {
        fieldId: fallbackFieldId,
        operator: fallbackOperator,
        value: fallbackValue,
        fieldType: fallbackFieldType,
        joinToNext: 'and',
      },
    ];

  return rows
    .map((row) => {
      const fieldId = cleanString(row?.fieldId || row?.field);
      if (!fieldId) return null;

      const normalizedFieldType = resolveComposerFieldType({ type: row?.fieldType }, fallbackFieldType || 'string');
      const normalizedOperator = normalizeComposerOperatorForFieldType(
        normalizedFieldType,
        normalizePredicateOperator(row?.operator || row?.op || 'eq'),
      );
      const isTemporalField = isComposerTemporalFieldType(normalizedFieldType);
      const temporalParts = isTemporalField
        ? normalizeTemporalPredicateParts({
          rawValue: row?.value,
          operator: normalizedOperator,
        })
        : null;
      const normalizedValue = temporalParts
        ? temporalParts.value
        : normalizeQueryPredicateValue({
          rawValue: row?.value,
          fieldType: normalizedFieldType,
          operator: normalizedOperator,
        });
      const normalizedUnit = temporalParts ? temporalParts.unit : undefined;

      if (normalizedValue === '' || normalizedValue === null) {
        if (normalizedOperator !== 'is_empty' && normalizedOperator !== 'is_not_empty') {
          return null;
        }
      }

      if (Array.isArray(normalizedValue) && normalizedValue.length === 0) {
        return null;
      }

      if (
        isTemporalField
        && isComposerTemporalOperatorNeedsValue(normalizedOperator)
        && (normalizedValue === '' || normalizedValue == null)
      ) {
        return null;
      }

      if (
        isTemporalField
        && isComposerTemporalOperatorNeedsUnit(normalizedOperator)
        && !normalizedUnit
      ) {
        return null;
      }

      if (
        normalizedOperator === 'between'
        && (
          !normalizedValue
          || typeof normalizedValue !== 'object'
          || normalizedValue.min === ''
          || normalizedValue.max === ''
          || normalizedValue.min == null
          || normalizedValue.max == null
        )
      ) {
        return null;
      }

      const predicate = {
        fieldId,
        op: normalizedOperator,
      };

      if (normalizedValue !== undefined) {
        predicate.value = normalizedValue;
      }

      if (normalizedUnit !== undefined) {
        predicate.unit = normalizedUnit;
      }

      predicate.joinToNext = cleanString(row?.joinToNext).toLowerCase() === 'or' ? 'or' : 'and';

      return predicate;
    })
    .filter(Boolean);
}

function buildQuerySortRows(querySorts) {
  if (!Array.isArray(querySorts) || querySorts.length === 0) return [];

  return querySorts
    .map((row) => {
      const fieldId = cleanString(row?.fieldId || row?.field);
      if (!fieldId) return null;

      const order = cleanString(row?.order || row?.direction || 'asc').toLowerCase() === 'desc'
        ? 'desc'
        : 'asc';

      return {
        fieldId,
        order,
      };
    })
    .filter(Boolean);
}

export function createComposerRequest({
  intent,
  typeId,
  nodeId,
  limit,
  fieldId,
  fieldType,
  operator,
  predicateValue,
  queryFilters,
  querySorts,
  writeTitle,
  writeValuesText,
}) {
  const safeLimit = parsePositiveInt(limit, 25);
  const cleanIntent = cleanString(intent).toLowerCase();
  const cleanTypeId = cleanString(typeId);
  const cleanNodeId = cleanString(nodeId);
  const cleanFieldId = cleanString(fieldId);
  const cleanOperator = cleanString(operator) || 'eq';
  const cleanTitle = cleanString(writeTitle);

  if (cleanIntent === 'list_types') {
    return {
      label: 'List types',
      method: 'GET',
      path: `/types?limit=${safeLimit}`,
      body: undefined,
      canTestRun: true,
      writeOperation: false,
      warnings: [],
    };
  }

  if (cleanIntent === 'list_fields') {
    if (!cleanTypeId) {
      return {
        error: 'Select a type to list its fields.',
      };
    }

    return {
      label: 'List fields for type',
      method: 'GET',
      path: `/types/${encodeURIComponent(cleanTypeId)}/fields`,
      body: undefined,
      canTestRun: true,
      writeOperation: false,
      warnings: [],
    };
  }

  if (cleanIntent === 'list_nodes') {
    const params = new URLSearchParams();
    params.set('limit', String(safeLimit));
    if (cleanTypeId) params.set('type', cleanTypeId);

    return {
      label: 'List nodes',
      method: 'GET',
      path: `/nodes?${params.toString()}`,
      body: undefined,
      canTestRun: true,
      writeOperation: false,
      warnings: cleanTypeId ? [] : ['No type filter selected; query will scan all node types.'],
    };
  }

  if (cleanIntent === 'query_nodes') {
    const where = buildQueryPredicates({
      queryFilters,
      fallbackFieldId: cleanFieldId,
      fallbackOperator: cleanOperator,
      fallbackValue: predicateValue,
      fallbackFieldType: fieldType,
    });

    const sort = buildQuerySortRows(querySorts);

    const body = {
      where,
      sort,
      limit: safeLimit,
    };

    if (cleanTypeId) {
      body.type = cleanTypeId;
    }

    return {
      label: 'Query nodes',
      method: 'POST',
      path: '/nodes/query',
      body,
      canTestRun: true,
      writeOperation: false,
      warnings: where.length === 0
        ? [cleanTypeId
          ? 'No predicates set; this will return the first page for the selected type.'
          : 'No predicates set; this will return the first page only.']
        : [],
    };
  }

  if (cleanIntent === 'get_node') {
    if (!cleanNodeId) {
      return {
        error: 'Enter a node id for the get-node request.',
      };
    }

    return {
      label: 'Get node by id',
      method: 'GET',
      path: `/nodes/${encodeURIComponent(cleanNodeId)}`,
      body: undefined,
      canTestRun: true,
      writeOperation: false,
      warnings: [],
    };
  }

  if (cleanIntent === 'create_node') {
    if (!cleanTypeId) {
      return {
        error: 'Select a type before generating a create-node request.',
      };
    }

    const parsedValues = parseJsonObject(writeValuesText, {});
    if (parsedValues.error) {
      return {
        error: `Values JSON error: ${parsedValues.error}`,
      };
    }

    return {
      label: 'Create node',
      method: 'POST',
      path: '/nodes',
      body: {
        type: cleanTypeId,
        title: cleanTitle || 'New node',
        values: parsedValues.value || {},
      },
      canTestRun: false,
      writeOperation: true,
      warnings: ['Write operations are generate-only in Composer v1 preview.'],
    };
  }

  if (cleanIntent === 'update_node') {
    if (!cleanNodeId) {
      return {
        error: 'Enter a node id before generating an update-node request.',
      };
    }

    const parsedValues = parseJsonObject(writeValuesText, {});
    if (parsedValues.error) {
      return {
        error: `Values JSON error: ${parsedValues.error}`,
      };
    }

    const body = {
      values: parsedValues.value || {},
    };

    if (cleanTitle) body.title = cleanTitle;

    return {
      label: 'Update node',
      method: 'PATCH',
      path: `/nodes/${encodeURIComponent(cleanNodeId)}`,
      body,
      canTestRun: false,
      writeOperation: true,
      warnings: [
        'PATCH and DELETE require If-Match (ETag) from a prior GET response.',
        'Write operations are generate-only in Composer v1 preview.',
      ],
    };
  }

  if (cleanIntent === 'delete_node') {
    if (!cleanNodeId) {
      return {
        error: 'Enter a node id before generating a delete-node request.',
      };
    }

    return {
      label: 'Delete node',
      method: 'DELETE',
      path: `/nodes/${encodeURIComponent(cleanNodeId)}`,
      body: undefined,
      canTestRun: false,
      writeOperation: true,
      warnings: [
        'PATCH and DELETE require If-Match (ETag) from a prior GET response.',
        'Write operations are generate-only in Composer v1 preview.',
      ],
    };
  }

  return {
    error: 'Unknown Composer intent selected.',
  };
}
